package com.swetdot.swetgram.data.cache

import app.cash.sqldelight.Query
import app.cash.sqldelight.Transacter
import app.cash.sqldelight.db.QueryResult
import app.cash.sqldelight.db.SqlCursor
import app.cash.sqldelight.db.SqlDriver
import app.cash.sqldelight.db.SqlPreparedStatement

/** Synchronous SQL.js adapter for the shared cache, with real SQLite transactions. */
internal class BrowserSqlDriver(private val database: dynamic) : SqlDriver {
    private val listeners = mutableMapOf<String, MutableSet<Query.Listener>>()
    private var transaction: Transaction? = null

    private fun prepare(sql: String, parameters: Int, binders: (SqlPreparedStatement.() -> Unit)?): dynamic {
        val statement = database.prepare(sql)
        try {
            val values = arrayOfNulls<Any>(parameters)
            binders?.invoke(object : SqlPreparedStatement {
                override fun bindString(index: Int, string: String?) { values[index] = string }
                override fun bindLong(index: Int, long: Long?) {
                    // All cache integers (timestamps, versions, counters) must fit JS's exact range.
                    require(long == null || long in -9007199254740991L..9007199254740991L)
                    values[index] = long?.toDouble()
                }
                override fun bindDouble(index: Int, double: Double?) { values[index] = double }
                override fun bindBoolean(index: Int, boolean: Boolean?) { values[index] = boolean?.let { if (it) 1 else 0 } }
                override fun bindBytes(index: Int, bytes: ByteArray?) {
                    values[index] = bytes?.let { source ->
                        val result: dynamic = js("new Uint8Array(source.length)")
                        source.indices.forEach { result[it] = source[it].toInt() and 255 }
                        result as Any
                    }
                }
            })
            statement.bind(values)
            return statement
        } catch (error: Throwable) {
            statement.free()
            throw error
        }
    }

    override fun <R> executeQuery(identifier: Int?, sql: String, mapper: (SqlCursor) -> QueryResult<R>, parameters: Int, binders: (SqlPreparedStatement.() -> Unit)?): QueryResult<R> {
        val statement = prepare(sql, parameters, binders)
        try {
            return mapper(object : SqlCursor {
                private var row: dynamic = null
                override fun next(): QueryResult<Boolean> {
                    val next = statement.step() as Boolean
                    row = if (next) statement.get() else null
                    return QueryResult.Value(next)
                }
                override fun getString(index: Int): String? = row[index] as String?
                override fun getLong(index: Int): Long? = (row[index] as Double?)?.toLong()
                override fun getDouble(index: Int): Double? = row[index] as Double?
                override fun getBoolean(index: Int): Boolean? = getLong(index)?.let { it != 0L }
                override fun getBytes(index: Int): ByteArray? {
                    val value = row[index] ?: return null
                    return ByteArray(value.length as Int) { (value[it] as Int).toByte() }
                }
            })
        } finally {
            statement.free()
        }
    }

    override fun execute(identifier: Int?, sql: String, parameters: Int, binders: (SqlPreparedStatement.() -> Unit)?): QueryResult<Long> {
        val statement = prepare(sql, parameters, binders)
        try {
            statement.step()
            return QueryResult.Value((database.getRowsModified() as Int).toLong())
        } finally {
            statement.free()
        }
    }

    private inner class Transaction(val parent: Transaction?) : Transacter.Transaction() {
        override val enclosingTransaction: Transacter.Transaction? get() = parent
        override fun endTransaction(successful: Boolean): QueryResult<Unit> {
            if (parent == null) database.run(if (successful) "COMMIT" else "ROLLBACK")
            transaction = parent
            return QueryResult.Unit
        }
    }

    override fun newTransaction(): QueryResult<Transacter.Transaction> {
        val parent = transaction
        if (parent == null) database.run("BEGIN TRANSACTION")
        val next = Transaction(parent)
        transaction = next
        return QueryResult.Value(next)
    }

    override fun currentTransaction(): Transacter.Transaction? = transaction
    override fun addListener(vararg queryKeys: String, listener: Query.Listener) {
        queryKeys.forEach { listeners.getOrPut(it) { mutableSetOf() }.add(listener) }
    }
    override fun removeListener(vararg queryKeys: String, listener: Query.Listener) {
        queryKeys.forEach { listeners[it]?.remove(listener) }
    }
    override fun notifyListeners(vararg queryKeys: String) {
        queryKeys.flatMap { listeners[it].orEmpty() }.toSet().forEach { it.queryResultsChanged() }
    }
    override fun close() {
        listeners.clear()
        database.close()
    }
}
